import { useState, useEffect, useRef, useCallback } from "react";

// ── API CONFIGURATION ─────────────────────────────────────────────────────────
// If REACT_APP_API_URL is not set, rely on CRA's "proxy" setting in package.json
// so relative requests like /api/... are forwarded to the backend.
const API_BASE = process.env.REACT_APP_API_URL || "";

function apiUrl(path) {
  const rawBase = String(API_BASE || "").trim();
  const base = rawBase.replace(/\/+$/, "");
  const p = String(path || "");
  const withLeadingSlash = p.startsWith("/") ? p : `/${p}`;

  if (!base) return withLeadingSlash;

  // If base already ends with /api and path starts with /api, drop one of them.
  if (/\/api$/i.test(base) && /^\/api(\/|$)/i.test(withLeadingSlash)) {
    return `${base}${withLeadingSlash.replace(/^\/api/i, "")}`;
  }

  return `${base}${withLeadingSlash}`;
}

// ── THEME ─────────────────────────────────────────────────────────────────────
const T = {
  appBg:        "#f1f5f9",
  card:         "#ffffff",
  card2:        "#f8fafc",
  border:       "#e2e8f0",
  border2:      "#cbd5e1",
  text:         "#0f172a",
  text2:        "#475569",
  muted:        "#94a3b8",
  accent:       "#b91c1c",
  accentSoft:   "#fef2f2",
  accentBorder: "#fecaca",
  primary:      "linear-gradient(135deg,#b91c1c,#991b1b)",
  primaryText:  "#ffffff",
  shadow:       "0 1px 3px rgba(0,0,0,0.07)",
  // Contract colors
  mears:    { bg:"#eff6ff", border:"#93c5fd", text:"#1e40af", strip:"#3b82f6" },
  goaa:     { bg:"#f5f3ff", border:"#c4b5fd", text:"#5b21b6", strip:"#7c3aed" },
  charter:  { bg:"#fef2f2", border:"#fca5a5", text:"#991b1b", strip:"#ef4444" },
  fixed:    { bg:"#f0fdf4", border:"#86efac", text:"#166534", strip:"#22c55e" },
  farmout:  { bg:"#f8fafc", border:"#cbd5e1", text:"#64748b", strip:"#94a3b8" },
};

// ── STATUS CONFIG ─────────────────────────────────────────────────────────────
const SC = {
  confirmed:  { label:"Confirmed",  color:"#16a34a", bg:"#f0fdf4", border:"#86efac" },
  pending:    { label:"Awaiting",   color:"#2563eb", bg:"#eff6ff", border:"#93c5fd" },
  declined:   { label:"Declined",   color:"#dc2626", bg:"#fef2f2", border:"#fca5a5" },
  not_sent:   { label:"Not Sent",   color:"#94a3b8", bg:"#f8fafc", border:"#e2e8f0" },
  changed:    { label:"\u2691 Changed", color:"#7c3aed", bg:"#f5f3ff", border:"#c4b5fd" },
  sent:       { label:"Awaiting",   color:"#2563eb", bg:"#eff6ff", border:"#93c5fd" },
  no_response:{ label:"No Response",color:"#d97706", bg:"#fef3c7", border:"#fcd34d" },
  cancelled:  { label:"Cancelled",  color:"#dc2626", bg:"#fef2f2", border:"#fca5a5" }, // [UI-1]
};

// ── UTILITY FUNCTIONS ─────────────────────────────────────────────────────────
function formatTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'UTC' });
}

function formatTime24(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const hours = String(d.getUTCHours()).padStart(2, '0');
  const mins = String(d.getUTCMinutes()).padStart(2, '0');
  return `${hours}:${mins}`;
}

function extractTripNumber(fullTripNumber) {
  if (!fullTripNumber) return "";
  const parts = String(fullTripNumber).split('-');
  return parts.length > 1 ? parts[parts.length - 1] : String(fullTripNumber);
}

function parseRoute(fullRoute) {
  if (!fullRoute) return { from: "", to: "" };

  const lines = String(fullRoute)
    .split(/\r?\n/)
    .filter(l => l.trim());

  let from = "";
  let to = "";

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line.startsWith("From:")) {
      from = line.replace("From:", "").trim();
    } else if (line.startsWith("To:")) {
      to = line.replace("To:", "").trim();
    }
  }

  return { from, to };
}

function inferContract(customerName) {
  if (!customerName) return "charter";
  const lower = customerName.toLowerCase();
  if (lower.includes("mears")) return "mears";
  if (lower.includes("goaa") || lower.includes("airport")) return "goaa";
  if (lower.includes("fixed")) return "fixed";
  return "charter";
}

function toLocalDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function normalizeStatus(currentStatus) {
  if (!currentStatus) return "not_sent";
  const s = String(currentStatus).toLowerCase();
  if (s === "awaiting" || s === "pending") return "not_sent";
  return s; // 'cancelled', 'sent', 'confirmed', 'declined' pass through
}

// A job is sendable if it isn't cancelled. [UI-5]
function isSendable(job) {
  return job.status !== "cancelled";
}

// ── DRIVER CONFIRM ROW ────────────────────────────────────────────────────────
function DriverConfirmRow({ driver, onToggle, onSendDriver, onSendJob, onReassignJob, isSelected, onToggleSelect }) {
  const allJobs    = driver.jobs;
  const confirmed  = allJobs.filter(j => j.status === "confirmed").length;
  const changed    = allJobs.filter(j => (j.status === "changed" || j.hasChanges) && j.status !== "cancelled").length;
  const declined   = allJobs.filter(j => j.status === "declined").length;
  const pending    = allJobs.filter(j => j.status === "pending" || j.status === "sent").length;
  const notSent    = allJobs.filter(j => !j.status || j.status === "not_sent").length;
  const cancelled  = allJobs.filter(j => j.status === "cancelled").length;
  const sendableJobs = allJobs.filter(isSendable);
  const allConfirmed = sendableJobs.length > 0 && confirmed === sendableJobs.length;

  return (
    <div style={{ background:T.card, border:`1px solid ${cancelled>0 ? "#fca5a5" : changed > 0 ? "#c4b5fd" : declined > 0 ? "#fca5a5" : T.border}`, borderRadius:10, marginBottom:6, overflow:"hidden", boxShadow:T.shadow }}>
      <div onClick={onToggle} style={{ display:"flex", alignItems:"center", gap:12, padding:"11px 14px", cursor:"pointer", background: changed>0?"#fdfbff": allConfirmed?"#f0fdf4":T.card, userSelect:"none" }}>
        <input type="checkbox" checked={isSelected}
          onChange={e => { e.stopPropagation(); onToggleSelect(); }}
          onClick={e => e.stopPropagation()}
          style={{ width:14, height:14, cursor:"pointer", accentColor:T.accent, flexShrink:0 }} />
        <div style={{ fontSize:10, color:T.muted, width:12, flexShrink:0, transition:"transform 0.15s", transform: driver.expanded?"rotate(90deg)":"rotate(0deg)" }}>{"\u25B6"}</div>
        <div style={{ width:32, height:32, borderRadius:"50%", flexShrink:0, background:T.accentSoft, border:`1.5px solid ${T.accentBorder}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, color:T.accent }}>
          {driver.init}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{driver.name}</div>
          <div style={{ fontSize:11, color:T.muted }}>{driver.phone}</div>
        </div>
        <div style={{ fontSize:11, color:T.text2, fontWeight:600, flexShrink:0 }}>
          {allJobs.length} job{allJobs.length!==1?"s":""}
        </div>
        <div style={{ display:"flex", gap:5, alignItems:"center", flexShrink:0 }}>
          {confirmed > 0 && <span style={{ fontSize:10, fontWeight:700, color:"#16a34a", background:"#f0fdf4", border:"1px solid #86efac", borderRadius:20, padding:"2px 8px" }}>{confirmed} Confirmed</span>}
          {pending > 0   && <span style={{ fontSize:10, fontWeight:700, color:"#2563eb", background:"#eff6ff", border:"1px solid #93c5fd", borderRadius:20, padding:"2px 8px" }}>{pending} Awaiting</span>}
          {changed > 0   && <span style={{ fontSize:10, fontWeight:700, color:"#7c3aed", background:"#f5f3ff", border:"1px solid #c4b5fd", borderRadius:20, padding:"2px 8px" }}>{"\u2691"} {changed} Changed</span>}
          {declined > 0  && <span style={{ fontSize:10, fontWeight:700, color:"#dc2626", background:"#fef2f2", border:"1px solid #fca5a5", borderRadius:20, padding:"2px 8px" }}>{declined} Declined</span>}
          {cancelled > 0 && <span style={{ fontSize:10, fontWeight:700, color:"#dc2626", background:"#fef2f2", border:"1px solid #fca5a5", borderRadius:20, padding:"2px 8px" }}>{cancelled} Cancelled</span>}
          {notSent > 0   && <span style={{ fontSize:10, fontWeight:700, color:"#94a3b8", background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:20, padding:"2px 8px" }}>{notSent} Not Sent</span>}
          {allConfirmed  && <span style={{ fontSize:10, fontWeight:700, color:"#16a34a" }}>{"\u2705"} ALL CONFIRMED</span>}
        </div>
        {sendableJobs.length > 0 && (
          <button onClick={e => { e.stopPropagation(); onSendDriver(driver.driverID); }}
            style={{ padding:"5px 14px", background: changed>0?"linear-gradient(135deg,#7c3aed,#6d28d9)":T.primary, border:"none", borderRadius:6, fontSize:11, color:"#fff", fontWeight:700, cursor:"pointer", flexShrink:0 }}>
            {changed > 0 ? "\u2691 Resend" : notSent === sendableJobs.length ? "Send SMS" : "Resend"}
          </button>
        )}
      </div>

      {driver.expanded && (
        <div style={{ borderTop:`1px solid ${T.border}` }}>
          {allJobs.map((job, ji) => {
            const isCancelled = job.status === "cancelled";
            const sc = SC[job.status] || SC.not_sent;
            const c  = isCancelled ? T.charter : (T[job.contract] || T.charter);
            const route = parseRoute(job.fullRoute);
            const tripNum = extractTripNumber(job.tripNumber);
            return (
              <div key={ji} style={{ borderBottom: ji < allJobs.length-1 ? `1px solid ${T.border}` : "none", background: isCancelled?"#fef2f2": job.hasChanges?"#fdfbff":"transparent" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px 10px 40px", opacity: isCancelled?0.85:1 }}>
                  <div style={{ width:3, height:46, borderRadius:2, background:c.strip, flexShrink:0 }}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:3, flexWrap:"wrap" }}>
                      <span style={{ fontSize:13, fontWeight:700, color:T.text, fontFamily:"monospace", textDecoration: isCancelled?"line-through":"none" }}>Trip #{tripNum}</span>
                      <span style={{ fontSize:10, color:T.muted }}>{"\u00B7"}</span>
                      <span style={{ fontSize:10, color:c.text, background:c.bg, border:`1px solid ${c.border}`, borderRadius:3, padding:"1px 5px", fontWeight:600 }}>{job.serviceTypeName}</span>
                      <span style={{ fontSize:10, color:T.muted }}>{"\u00B7"}</span>
                      <span style={{ fontSize:10, color:T.text2, fontWeight:600 }}>{job.customerName}</span>
                    </div>
                    <div style={{ fontSize:11, color:T.text2, marginBottom:4, textDecoration: isCancelled?"line-through":"none" }}>
                      {[
                        job.reportDateTime && `${formatTime24(job.reportDateTime)} Report`,
                        job.spotDateTime && `${formatTime24(job.spotDateTime)} Spot`,
                        job.pickupDateTime && `${formatTime24(job.pickupDateTime)} Start`,
                        job.dropOffDateTime && `${formatTime24(job.dropOffDateTime)} End`
                      ].filter(Boolean).join("  \u2022  ")}
                    </div>
                    <div style={{ fontSize:11, color:T.text2, lineHeight:1.5, marginBottom:3 }}>
                      {route.from && <div><strong>From:</strong> {route.from}</div>}
                      {route.to && <div><strong>To:</strong> {route.to}</div>}
                    </div>
                    {(job.notes || job.specialInstructions) && (
                      <div style={{ fontSize:10, color:T.muted, marginBottom:3, lineHeight:1.4 }}>
                        {job.notes && <div style={{ marginBottom: job.specialInstructions ? 4 : 0 }}><strong>Notes:</strong> {job.notes}</div>}
                        {job.specialInstructions && <div><strong>Instructions:</strong> {job.specialInstructions}</div>}
                      </div>
                    )}
                    <div style={{ fontSize:10, color:T.muted, marginTop:3 }}>
                      {job.vehicleAssignment && String(job.vehicleAssignment).trim() !== "-" && `\u{1F68C} Bus ${job.vehicleAssignment}`}
                      {job.vehicleAssignment && String(job.vehicleAssignment).trim() !== "-" && job.paxCount > 0 ? "  \u00B7  " : ""}
                      {job.paxCount > 0 && `\u{1F465} ${job.paxCount} pax`}
                      {job.lastSentDateTime && <span style={{ marginLeft:8, color:sc.color }}>Sent {formatTime(job.lastSentDateTime)}</span>}
                    </div>
                  </div>
                  <div style={{ padding:"4px 12px", borderRadius:20, fontSize:10, fontWeight:700, color:sc.color, background:sc.bg, border:`1px solid ${sc.border}`, flexShrink:0 }}>
                    {sc.label}
                  </div>
                  {/* [UI-3][UI-5] No Send button for cancelled jobs */}
                  {!isCancelled && (
                    <button onClick={() => onSendJob(job.assignmentID)}
                      style={{ padding:"4px 11px", background: job.hasChanges?"#f5f3ff":T.card2, border:`1px solid ${job.hasChanges?"#c4b5fd":T.border}`, borderRadius:5, fontSize:10, color: job.hasChanges?"#7c3aed":T.text2, fontWeight: job.hasChanges?700:400, cursor:"pointer", flexShrink:0 }}>
                      {job.status === "not_sent" || !job.status ? "Send" : job.hasChanges ? "\u2691 Resend" : "Resend"}
                    </button>
                  )}
                </div>

                {isCancelled && (
                  <div style={{ margin:"0 14px 10px 40px", padding:"8px 12px", background:"#fef2f2", border:"1px solid #fca5a5", borderRadius:7, fontSize:11, color:"#991b1b", display:"flex", alignItems:"center", gap:8 }}>
                    <span>{"\u26A0\uFE0F"}</span>
                    <span><strong>Cancelled in TourMaster</strong> {job.lastSentDateTime ? "\u2014 driver was notified" : ""}</span>
                  </div>
                )}

                {!isCancelled && job.hasChanges && (
                  <div style={{ margin:"0 14px 10px 40px", padding:"8px 12px", background:"#f5f3ff", border:"1px solid #c4b5fd", borderRadius:7, display:"flex", alignItems:"flex-start", gap:10 }}>
                    <div style={{ flexShrink:0, marginTop:1 }}>
                      <div style={{ width:18, height:18, borderRadius:"50%", background:"#7c3aed", display:"flex", alignItems:"center", justifyContent:"center" }}>
                        <span style={{ fontSize:10, color:"#fff", fontWeight:900 }}>{"\u2691"}</span>
                      </div>
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:"#5b21b6", marginBottom:3 }}>Change Detected {"\u2014"} Driver Needs Re-Confirmation</div>
                      <div style={{ fontSize:11, color:"#6d28d9" }}>Job details have been modified in TourMaster</div>
                    </div>
                  </div>
                )}

                {job.status === "declined" && (
                  <div style={{ margin:"0 14px 10px 40px", padding:"7px 12px", background:"#fef2f2", border:"1px solid #fca5a5", borderRadius:7, fontSize:11, color:"#991b1b", display:"flex", alignItems:"center", gap:8 }}>
                    <span>{"\u{1F4DE}"}</span>
                    <span><strong>Follow-up needed</strong> {"\u2014"} driver declined this assignment</span>
                    <button onClick={() => onReassignJob(job.assignmentID)}
                      style={{ marginLeft:"auto", padding:"3px 10px", background:T.accent, border:"none", borderRadius:5, fontSize:10, color:"#fff", fontWeight:700, cursor:"pointer" }}>
                      Reassign
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── CONFIRMATIONS ─────────────────────────────────────────────────────────────
function Confirmations({ jobs, drivers, stats, reload, confirmDate, setConfirmDate }) {
  const [expandedSet, setExpandedSet]   = useState(new Set([0, 1, 2]));
  const [statFilter, setStatFilter]     = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [toast, setToast]               = useState(null);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [importing, setImporting] = useState(false);
  const [syncingDrivers, setSyncingDrivers] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [selectedDrivers, setSelectedDrivers] = useState(new Set());
  const [lastUpdate, setLastUpdate]     = useState(new Date());
  const datePickerRef = useRef(null);

  // App owns the confirmDate so that API reloads and UI stay in sync.

  const driverJobMap = {};
  jobs.forEach(job => {
    if (!job.driverID) return;
    if (!driverJobMap[job.driverID]) driverJobMap[job.driverID] = [];
    driverJobMap[job.driverID].push(job);
  });

  const confirmDrivers = drivers
    .map((d, idx) => ({
      ...d,
      driverIdx: idx,
      expanded: expandedSet.has(idx),
      jobs: driverJobMap[d.driverID] || []
    }))
    .filter(d => d.jobs.length > 0);

  const allJobs      = confirmDrivers.flatMap(d => d.jobs);
  const totalChanged = allJobs.filter(j => j.hasChanges && j.status !== "cancelled").length;

  // [UI-2] Cancelled tile added; Not Sent excludes cancelled.
  const STAT_DEFS = [
    { key:null,        label:"Total",     n: stats?.totalAssignments || 0, color:T.text },
    { key:"confirmed", label:"Confirmed", n: stats?.confirmedCount || 0,   color:"#16a34a" },
    { key:"sent",      label:"Awaiting",  n: stats?.sentCount || 0,        color:"#2563eb" },
    { key:"changed",   label:"Changed",   n: stats?.needsUpdateCount || 0, color:"#7c3aed" },
    { key:"cancelled", label:"Cancelled", n: stats?.cancelledCount || 0,   color:"#dc2626" },
    { key:"declined",  label:"Declined",  n: stats?.declinedCount || 0,    color:"#dc2626" },
    { key:"not_sent",  label:"Not Sent",  n: Math.max(0, (stats?.totalAssignments || 0) - ((stats?.confirmedCount || 0) + (stats?.pendingCount || 0) + (stats?.sentCount || 0) + (stats?.declinedCount || 0) + (stats?.cancelledCount || 0))), color:T.muted },
  ];

  const visibleDrivers = statFilter
    ? confirmDrivers
        .map(d => ({ ...d, jobs: d.jobs.filter(j => j.status === statFilter || (statFilter === "changed" && j.hasChanges && j.status !== "cancelled")) }))
        .filter(d => d.jobs.length > 0)
    : confirmDrivers;

  function toggleDriver(driverIdx) {
    setExpandedSet(prev => { const n = new Set(prev); n.has(driverIdx) ? n.delete(driverIdx) : n.add(driverIdx); return n; });
  }

  function toggleAllDrivers(expand) {
    setExpandedSet(expand ? new Set(confirmDrivers.map(d => d.driverIdx)) : new Set());
  }

  function showToast(msg, color) {
    setToast({ msg, color: color || "#16a34a" });
    setTimeout(() => setToast(null), 3000);
  }

  async function handleImportAssignments() {
    if (importing) return;
    try {
      setImporting(true);
      const response = await fetch(apiUrl('/api/import/assignments'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetDate: toLocalDateStr(confirmDate) })
      });
      const result = await response.json();
      if (!response.ok) {
        const errorMsg = result?.errors?.[0] || "Import failed";
        showToast(`❌ ${errorMsg}`, "#dc2626");
      } else {
        showToast(`⬇ Imported ${result.importedCount || 0} assignment${(result.importedCount || 0) !== 1 ? 's' : ''}`, "#16a34a");
      }
      await reload();
    } catch (error) {
      console.error('Import assignments error:', error);
      showToast("❌ Import failed", "#dc2626");
    } finally {
      setImporting(false);
    }
  }

  async function handleSyncDrivers() {
    if (syncingDrivers) return;
    try {
      setSyncingDrivers(true);
      const response = await fetch(apiUrl('/api/import/sync-drivers'), { method: 'POST' });
      const result = await response.json();
      if (!response.ok) {
        showToast("❌ Sync failed", "#dc2626");
      } else {
        showToast(result.message || `✓ Synced ${result.syncedCount || 0} drivers`, "#16a34a");
      }
      await reload();
    } catch (error) {
      console.error('Sync drivers error:', error);
      showToast("❌ Sync failed", "#dc2626");
    } finally {
      setSyncingDrivers(false);
    }
  }

  // Fetch current assignments directly (so we can compute pre/post-import diff).
  async function fetchAssignmentsNow() {
    const dateStr = toLocalDateStr(confirmDate);
    const resp = await fetch(apiUrl(`/api/dashboard/assignments?serviceDate=${dateStr}`));
    const data = await resp.json();
    return (data || []).map(a => ({ ...a, status: normalizeStatus(a.currentStatus) }));
  }

  // [UI-4] Send All: refresh from TM first, pause if anything left Booked, then send.
  async function handleSendAll() {
    if (sendingAll) return;
    try {
      setSendingAll(true);

      // Snapshot the jobs we intend to send (not-yet-sent, sendable) BEFORE refresh.
      const beforeToSend = new Set(
        allJobs.filter(j => isSendable(j) && (j.status === "not_sent")).map(j => j.assignmentID)
      );

      // Refresh local from TM (Booked-only). Cancelled/Quote unsent jobs drop out;
      // already-sent cancellations get flagged 'cancelled' by the monitor.
      showToast("\u{1F504} Refreshing from TourMaster...", "#2563eb");
      const impResp = await fetch(apiUrl('/api/import/assignments'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetDate: toLocalDateStr(confirmDate) })
      });
      const impResult = await impResp.json().catch(() => ({}));
      if (!impResp.ok) {
        showToast(`\u274C Refresh failed: ${impResult?.errors?.[0] || 'unknown'}`, "#dc2626");
        return;
      }

      // Recompute what's still sendable after refresh.
      const after = await fetchAssignmentsNow();
      const afterSendable = new Set(
        after.filter(j => j.status !== "cancelled" && j.status === "not_sent").map(j => j.assignmentID)
      );

      const dropped = [...beforeToSend].filter(id => !afterSendable.has(id));
      const remaining = afterSendable.size;

      if (dropped.length > 0) {
        const proceed = window.confirm(
          `${dropped.length} job${dropped.length !== 1 ? 's are' : ' is'} no longer booked and ` +
          `${dropped.length !== 1 ? 'were' : 'was'} dropped.\n\n` +
          `Send to the remaining ${remaining} job${remaining !== 1 ? 's' : ''}?`
        );
        if (!proceed) { await reload(); return; }
      }

      const response = await fetch(apiUrl(`/api/messages/send-all?serviceDate=${toLocalDateStr(confirmDate)}`), { method: 'POST' });
      const result = await response.json();
      showToast(`\u{1F4E4} ${result.successCount || 0} messages sent`);
      await reload();
    } catch (error) {
      console.error('Send all error:', error);
      showToast("\u274C Failed to send", "#dc2626");
    } finally { setSendingAll(false); }
  }

  async function handleSendUpdates() {
    try {
      const changedJobs = allJobs.filter(j => j.hasChanges && j.status !== "cancelled");
      if (changedJobs.length === 0) return;

      const assignmentIDs = changedJobs.map(j => j.assignmentID);
      const response = await fetch(apiUrl('/api/messages/send'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignmentIDs, resendNoResponse: true })
      });
      const result = await response.json();
      showToast(`⚑ ${result.successCount || 0} update messages sent`, "#7c3aed");
      await reload();
    } catch (error) {
      console.error('Send updates error:', error);
      showToast("❌ Failed to send updates", "#dc2626");
    }
  }

  async function handleSendDriver(driverID) {
    try {
      const driverJobs = allJobs.filter(j => j.driverID === driverID && isSendable(j)); // [UI-5]
      const assignmentIDs = driverJobs.map(j => j.assignmentID);
      if (assignmentIDs.length === 0) { showToast("\u26A0 No sendable jobs", "#d97706"); return; }

      const response = await fetch(apiUrl('/api/messages/send'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignmentIDs, resendNoResponse: false })
      });
      const result = await response.json();
      showToast(`📤 ${result.successCount || 0} messages sent`);
      await reload();
    } catch (error) {
      console.error('Send driver error:', error);
      showToast("❌ Failed to send", "#dc2626");
    }
  }

  async function handleSendJob(assignmentID) {
    try {
      await fetch(apiUrl('/api/messages/send'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignmentIDs: [assignmentID], resendNoResponse: false })
      });
      showToast(`📤 Message sent`);
      await reload();
    } catch (error) {
      console.error('Send job error:', error);
      showToast("❌ Failed to send", "#dc2626");
    }
  }

  function handleReassignJob(assignmentID) {
    setReassignTarget({ assignmentID });
  }

  function confirmReassign() {
    showToast("🔄 Reassignment - feature pending backend implementation", "#d97706");
    setReassignTarget(null);
  }

  function toggleSelectDriver(driverIdx) {
    setSelectedDrivers(prev => { const n = new Set(prev); n.has(driverIdx) ? n.delete(driverIdx) : n.add(driverIdx); return n; });
  }

  // [SELECT-ALL] Select or clear every driver currently visible (respects the
  // active stat filter). If all visible are already selected, this clears them.
  function selectAllVisible() {
    const visibleIdx = visibleDrivers.map(d => d.driverIdx);
    setSelectedDrivers(prev => {
      const allSelected = visibleIdx.length > 0 && visibleIdx.every(i => prev.has(i));
      if (allSelected) {
        const n = new Set(prev);
        visibleIdx.forEach(i => n.delete(i));
        return n;
      }
      const n = new Set(prev);
      visibleIdx.forEach(i => n.add(i));
      return n;
    });
  }

  async function handleSendSelected() {
    if (selectedDrivers.size === 0) { showToast("⚠ No drivers selected", "#d97706"); return; }

    try {
      const selectedJobs = allJobs.filter(j => {
        const driver = confirmDrivers.find(d => d.driverID === j.driverID);
        return driver && selectedDrivers.has(driver.driverIdx) && isSendable(j); // [UI-5]
      });
      const assignmentIDs = selectedJobs.map(j => j.assignmentID);
      if (assignmentIDs.length === 0) { showToast("\u26A0 No sendable jobs in selection", "#d97706"); return; }

      const response = await fetch(apiUrl('/api/messages/send'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignmentIDs, resendNoResponse: false })
      });
      const result = await response.json();
      showToast(`📤 ${result.successCount || 0} messages sent to ${selectedDrivers.size} driver${selectedDrivers.size !== 1 ? "s" : ""}`, "#16a34a");
      setSelectedDrivers(new Set());
      await reload();
    } catch (error) {
      console.error('Send selected error:', error);
      showToast("❌ Failed to send", "#dc2626");
    }
  }

  async function handleCheckChanges() {
    try {
      const dateStr = toLocalDateStr(confirmDate);
      const response = await fetch(apiUrl(`/api/dashboard/check-changes?serviceDate=${dateStr}`), { method: 'POST' });
      const result = await response.json();
      showToast(result.message || "✓ Change detection complete", "#7c3aed");
      await reload();
    } catch (error) {
      console.error('Check changes error:', error);
      showToast("❌ Failed to check changes", "#dc2626");
    }
  }

  const MONTHS    = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const DOW_SHORT = ["Su","Mo","Tu","We","Th","Fr","Sa"];
  const [calMonth, setCalMonth] = useState(new Date());

  function calDays(m) {
    const first = new Date(m.getFullYear(), m.getMonth(), 1).getDay();
    const total = new Date(m.getFullYear(), m.getMonth()+1, 0).getDate();
    return { first, total };
  }

  function fmtDate(d) {
    const DAYS_LONG = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
    return `${DAYS_LONG[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  }

  function isToday(d) { const t = new Date(); t.setHours(0,0,0,0); return d.getTime() === t.getTime(); }
  function isSel(d)   { return d.getFullYear()===confirmDate.getFullYear()&&d.getMonth()===confirmDate.getMonth()&&d.getDate()===confirmDate.getDate(); }

  useEffect(() => {
    function onDoc(e) {
      if (!showDatePicker) return;
      if (!datePickerRef.current) return;
      if (!datePickerRef.current.contains(e.target)) {
        setShowDatePicker(false);
      }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showDatePicker]);

  useEffect(() => {
    setLastUpdate(new Date());
  }, [jobs, drivers, stats]);

  return (
    <div style={{ flex:1, overflowY:"auto", padding:16, position:"relative", background:T.appBg, minHeight:"100vh" }}>
      {toast && (
        <div style={{ position:"fixed", bottom:24, left:"50%", transform:"translateX(-50%)", background:toast.color, color:"#fff", padding:"10px 20px", borderRadius:8, fontSize:12, fontWeight:700, zIndex:300, boxShadow:"0 4px 20px rgba(0,0,0,0.2)" }}>
          {toast.msg}
        </div>
      )}

      {reassignTarget && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.35)", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:T.card, borderRadius:14, padding:28, width:360, boxShadow:"0 12px 40px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize:15, fontWeight:700, color:T.text, marginBottom:8 }}>Reassign Job?</div>
            <div style={{ fontSize:12, color:T.text2, marginBottom:20, lineHeight:1.5 }}>
              This feature requires backend implementation to handle reassignment workflow.
            </div>
            <div style={{ display:"flex", justifyContent:"flex-end", gap:10 }}>
              <button
                onClick={() => setReassignTarget(null)}
                style={{ padding:"8px 18px", background:T.card, border:`1px solid ${T.border}`, borderRadius:7, fontSize:12, color:T.text2, cursor:"pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={confirmReassign}
                style={{ padding:"8px 18px", background:T.primary, border:"none", borderRadius:7, fontSize:12, color:"#fff", fontWeight:700, cursor:"pointer" }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14, flexWrap:"wrap" }}>
        <span style={{ fontSize:12, color:T.text2, fontWeight:500 }}>Jobs for:</span>

        <div style={{ position:"relative" }} ref={datePickerRef}>
          <button
            onClick={() => setShowDatePicker(p => !p)}
            style={{ padding:"5px 12px", background:T.card, border:`1px solid ${showDatePicker?T.accent:T.border}`, borderRadius:6, fontSize:12, color:T.text, fontWeight:600, cursor:"pointer", boxShadow:T.shadow, display:"flex", alignItems:"center", gap:6 }}
          >
            📅 {fmtDate(confirmDate)}
          </button>

          {showDatePicker && (
            <div style={{ position:"absolute", top:"110%", left:0, zIndex:100, background:T.card, border:`1px solid ${T.border}`, borderRadius:10, boxShadow:"0 8px 32px rgba(0,0,0,0.15)", padding:12, width:240 }}>
              <div style={{ display:"flex", alignItems:"center", marginBottom:8, gap:4 }}>
                <button
                  onClick={() => setCalMonth(m => new Date(m.getFullYear(), m.getMonth()-1, 1))}
                  style={{ width:24, height:24, border:`1px solid ${T.border}`, borderRadius:4, background:T.card2, cursor:"pointer", fontSize:12 }}
                >
                  ‹
                </button>
                <div style={{ flex:1, textAlign:"center", fontSize:11, fontWeight:700, color:T.text }}>{MONTHS[calMonth.getMonth()]} {calMonth.getFullYear()}</div>
                <button
                  onClick={() => setCalMonth(m => new Date(m.getFullYear(), m.getMonth()+1, 1))}
                  style={{ width:24, height:24, border:`1px solid ${T.border}`, borderRadius:4, background:T.card2, cursor:"pointer", fontSize:12 }}
                >
                  ›
                </button>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2, marginBottom:4 }}>
                {DOW_SHORT.map(d => <div key={d} style={{ textAlign:"center", fontSize:9, fontWeight:700, color:T.muted, padding:"2px 0" }}>{d}</div>)}
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2 }}>
                {Array.from({length: calDays(calMonth).first}).map((_,i) => <div key={i}/>)}
                {Array.from({length: calDays(calMonth).total}).map((_,i) => {
                  const d = new Date(calMonth.getFullYear(), calMonth.getMonth(), i+1);
                  const sel = isSel(d), tod = isToday(d);
                  return (
                    <button
                      key={i}
                      onClick={() => { setConfirmDate(d); setShowDatePicker(false); }}
                      style={{ padding:"4px 0", border:`1px solid ${sel?T.accent:tod?"#fca5a5":"transparent"}`, borderRadius:5, background:sel?T.accent:"transparent", color:sel?"#fff":tod?T.accent:T.text, fontSize:11, cursor:"pointer", fontWeight:sel||tod?700:400 }}
                    >
                      {i+1}
                    </button>
                  );
                })}
              </div>

              <div style={{ marginTop:8, textAlign:"center" }}>
                <button
                  onClick={() => { const t = new Date(); t.setHours(0,0,0,0); setConfirmDate(t); setShowDatePicker(false); }}
                  style={{ fontSize:10, padding:"3px 12px", border:`1px solid ${T.border}`, borderRadius:10, background:T.card2, color:T.text2, cursor:"pointer" }}
                >
                  Today
                </button>
              </div>
            </div>
          )}
        </div>

        <div style={{ flex:1 }}/>

        <button
          onClick={handleSyncDrivers}
          disabled={syncingDrivers}
          style={{ padding:"6px 14px", background:T.card, border:`1px solid ${T.border}`, borderRadius:6, fontSize:11, color:T.text2, fontWeight:600, cursor: syncingDrivers ? "not-allowed" : "pointer", opacity: syncingDrivers ? 0.6 : 1 }}
        >
          {syncingDrivers ? "🔄 Syncing..." : "🔄 Sync Drivers"}
        </button>

        <button
          onClick={handleImportAssignments}
          disabled={importing}
          style={{ padding:"6px 14px", background:T.card, border:`1px solid ${T.border}`, borderRadius:6, fontSize:11, color:T.text2, fontWeight:600, cursor: importing ? "not-allowed" : "pointer", opacity: importing ? 0.6 : 1 }}
        >
          {importing ? "⬇ Importing..." : "⬇ Import"}
        </button>

        <button
          onClick={handleCheckChanges}
          style={{ padding:"6px 14px", background:T.card, border:`1px solid ${T.border}`, borderRadius:6, fontSize:11, color:T.text2, fontWeight:600, cursor:"pointer" }}
        >
          🔄 Check Now
        </button>

        {totalChanged > 0 && (
          <button
            onClick={handleSendUpdates}
            style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 14px", background:"#f5f3ff", border:"1px solid #c4b5fd", borderRadius:6, fontSize:11, color:"#7c3aed", fontWeight:700, cursor:"pointer" }}
          >
            ⚑ Send {totalChanged} Update{totalChanged!==1?"s":""}
          </button>
        )}

        <button
          onClick={handleSendSelected}
          style={{ padding:"6px 14px", background: selectedDrivers.size>0?T.accentSoft:T.card, border:`1px solid ${selectedDrivers.size>0?T.accentBorder:T.border}`, borderRadius:6, fontSize:11, color: selectedDrivers.size>0?T.accent:T.text2, fontWeight: selectedDrivers.size>0?700:600, cursor:"pointer" }}
        >
          {selectedDrivers.size > 0 ? `📤 Send to ${selectedDrivers.size} Selected` : "Send Selected"}
        </button>

        <button
          onClick={handleSendAll}
          disabled={sendingAll}
          style={{ padding:"6px 16px", background:T.primary, border:"none", borderRadius:6, fontSize:11, color:T.primaryText, fontWeight:700, cursor: sendingAll?"not-allowed":"pointer", opacity: sendingAll?0.6:1 }}
        >
          {sendingAll ? "\u{1F504} Sending..." : "\u{1F4E4} Send All"}
        </button>
      </div>

      <div style={{ display:"flex", gap:8, marginBottom:14 }}>
        {STAT_DEFS.map((s,i) => {
          const active = statFilter === s.key;
          return (
            <button
              key={i}
              onClick={() => setStatFilter(active ? null : s.key)}
              style={{ flex:1, padding:"10px 12px", background: active ? s.color : T.card, border:`2px solid ${active ? s.color : T.border}`, borderRadius:10, textAlign:"center", boxShadow:T.shadow, cursor:"pointer", transition:"all 0.15s" }}
            >
              <div style={{ fontSize:22, fontWeight:700, color: active?"#fff":s.color, letterSpacing:"-0.02em" }}>{s.n}</div>
              <div style={{ fontSize:9, color: active?"rgba(255,255,255,0.85)":T.muted, fontWeight:700, marginTop:2, letterSpacing:"0.06em" }}>{s.label.toUpperCase()}</div>
            </button>
          );
        })}
      </div>

      {statFilter && (
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10, padding:"7px 12px", background:T.accentSoft, border:`1px solid ${T.accentBorder}`, borderRadius:7 }}>
          <span style={{ fontSize:12, color:T.accent, fontWeight:600 }}>
            Showing: {STAT_DEFS.find(s=>s.key===statFilter)?.label}
          </span>
          <button
            onClick={() => setStatFilter(null)}
            style={{ marginLeft:"auto", fontSize:10, padding:"2px 10px", border:`1px solid ${T.accentBorder}`, borderRadius:10, background:"transparent", color:T.accent, cursor:"pointer" }}
          >
            Clear filter ×
          </button>
        </div>
      )}

      {totalChanged > 0 && !statFilter && (
        <div style={{ padding:"9px 14px", background:"#f5f3ff", border:"1px solid #c4b5fd", borderRadius:8, marginBottom:12, display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:8, height:8, borderRadius:"50%", background:"#7c3aed", boxShadow:"0 0 0 3px #7c3aed33", flexShrink:0 }}/>
          <span style={{ fontSize:12, fontWeight:700, color:"#7c3aed" }}>
            {totalChanged} job{totalChanged!==1?"s":""} have changes — drivers need updated confirmation
          </span>
        </div>
      )}

      <div style={{ display:"flex", alignItems:"center", marginBottom:8, gap:10 }}>
        {(() => {
          const visibleIdx = visibleDrivers.map(d => d.driverIdx);
          const allSelected = visibleIdx.length > 0 && visibleIdx.every(i => selectedDrivers.has(i));
          const someSelected = visibleIdx.some(i => selectedDrivers.has(i));
          return (
            <button onClick={selectAllVisible}
              style={{ display:"flex", alignItems:"center", gap:7, padding:"3px 10px", background: someSelected?T.accentSoft:T.card, border:`1px solid ${someSelected?T.accentBorder:T.border}`, borderRadius:5, cursor:"pointer", flexShrink:0 }}>
              <span style={{ width:14, height:14, borderRadius:3, border:`1.5px solid ${allSelected||someSelected?T.accent:T.border2}`, background: allSelected?T.accent:"transparent", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, color:"#fff", fontWeight:900, lineHeight:1 }}>
                {allSelected ? "\u2713" : someSelected ? "\u2212" : ""}
              </span>
              <span style={{ fontSize:10, color: someSelected?T.accent:T.text2, fontWeight:700, letterSpacing:"0.04em" }}>
                {allSelected ? "DESELECT ALL" : "SELECT ALL"}
              </span>
            </button>
          );
        })()}
        <div style={{ fontSize:10, color:T.muted, fontWeight:700, letterSpacing:"0.07em", flex:1 }}>
          {fmtDate(confirmDate).toUpperCase().split(",")[0]} {"\u2014"} {visibleDrivers.length} DRIVER{visibleDrivers.length!==1?"S":""} {"\u00B7"} {visibleDrivers.flatMap(d=>d.jobs).length} ASSIGNMENT{visibleDrivers.flatMap(d=>d.jobs).length!==1?"S":""}
        </div>
        <div style={{ display:"flex", gap:5 }}>
          <button onClick={() => toggleAllDrivers(true)} style={{ padding:"3px 9px", background:T.card, border:`1px solid ${T.border}`, borderRadius:5, fontSize:10, color:T.text2, fontWeight:600, cursor:"pointer" }}>{"\u229E"} Expand All</button>
          <button onClick={() => toggleAllDrivers(false)} style={{ padding:"3px 9px", background:T.card, border:`1px solid ${T.border}`, borderRadius:5, fontSize:10, color:T.text2, fontWeight:600, cursor:"pointer" }}>{"\u229F"} Collapse All</button>
        </div>
      </div>

      {visibleDrivers.length === 0
        ? <div style={{ textAlign:"center", padding:"32px 16px", color:T.muted, fontSize:13 }}>No jobs match this filter</div>
        : visibleDrivers.map((driver) => (
          <DriverConfirmRow
            key={driver.driverIdx}
            driver={driver}
            onToggle={() => toggleDriver(driver.driverIdx)}
            onSendDriver={handleSendDriver}
            onSendJob={handleSendJob}
            onReassignJob={handleReassignJob}
            isSelected={selectedDrivers.has(driver.driverIdx)}
            onToggleSelect={() => toggleSelectDriver(driver.driverIdx)}
          />
        ))
      }

      <div style={{ textAlign:"center", fontSize:10, color:T.muted, marginTop:12 }}>
        Last updated: {lastUpdate.toLocaleTimeString()}
      </div>
    </div>
  );
}

// ── APP WRAPPER ───────────────────────────────────────────────────────────────
export default function App() {
  const [jobs, setJobs] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [stats, setStats] = useState(null);
  const [confirmDate, setConfirmDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
  });
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const dateStr = toLocalDateStr(confirmDate);

      const driversResp = await fetch(apiUrl('/api/drivers'));
      const driversData = await driversResp.json();
      setDrivers((driversData || []).map(d => {
        const name = d.driverName || "";
        const short = d.shortName || "";
        const init = short
          ? short
              .split(/\s+/)
              .filter(Boolean)
              .map(n => n[0])
              .join("")
          : name
              .split(/\s+/)
              .filter(Boolean)
              .map(n => n[0])
              .join("");

        return {
          driverID: d.driverID,
          name,
          init: init || "?",
          phone: d.cellPhone || d.homePhone || "",
        };
      }));

      const assignmentsResp = await fetch(apiUrl(`/api/dashboard/assignments?serviceDate=${dateStr}`));
      const assignmentsData = await assignmentsResp.json();
      setJobs((assignmentsData || []).map(a => ({
        ...a,
        status: normalizeStatus(a.currentStatus),
        contract: inferContract(a.customerName)
      })));

      const statsResp = await fetch(apiUrl(`/api/dashboard/stats?serviceDate=${dateStr}`));
      const statsData = await statsResp.json();
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, [confirmDate]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    const interval = setInterval(loadAll, 30000);
    return () => clearInterval(interval);
  }, [loadAll]);

  if (loading && jobs.length === 0) {
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", background:T.appBg }}>
        <div style={{ fontSize:14, color:T.muted }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100vh", background:T.appBg, fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <div style={{ padding:"12px 20px", background:T.card, borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", gap:12 }}>
        <div style={{ width:8, height:8, borderRadius:"50%", background:T.accent }}/>
        <span style={{ fontSize:13, fontWeight:700, color:T.text }}>Driver Confirmations</span>
        <span style={{ fontSize:11, color:T.muted }}>— Empire Coach Line</span>
      </div>
      <Confirmations
        jobs={jobs}
        drivers={drivers}
        stats={stats}
        reload={loadAll}
        confirmDate={confirmDate}
        setConfirmDate={setConfirmDate}
      />
    </div>
  );
}
